import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, FileText, Calendar, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ThankYou = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingQuote, setGeneratingQuote] = useState(false);

  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/clients/${clientId}`);
        setClient(response.data);
      } catch (error) {
        console.error('Error fetching client:', error);
      }
      setLoading(false);
    };
    fetchClient();
  }, [clientId]);

  const handleGenerateQuote = async () => {
    if (!client) return;
    
    setGeneratingQuote(true);
    try {
      // Generate a basic quote based on the branch
      const basePrice = {
        formation: 299.99,
        pme: 499.99,
        freelance: 149.99,
        employes: 79.99
      };

      const quoteData = {
        client_id: client.id,
        items: [
          {
            description: `Agent IA ${client.branch} - Configuration initiale`,
            quantity: 1,
            unit_price: basePrice[client.branch] || 199.99
          },
          {
            description: 'Support et maintenance (12 mois)',
            quantity: 1,
            unit_price: 99.99
          }
        ],
        notes: `Devis généré pour: ${client.needs.substring(0, 100)}...`
      };

      const response = await axios.post(`${API}/quotes`, quoteData);
      window.location.href = `/quote/${response.data.id}`;
    } catch (error) {
      console.error('Error generating quote:', error);
    }
    setGeneratingQuote(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="thank-you-page">
      <div className="max-w-2xl mx-auto px-6 text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>

        {/* Thank You Message */}
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4 animate-fade-in delay-100">
          {t('thankYou')}
        </h1>
        <p className="text-slate-600 text-lg mb-8 animate-fade-in delay-200">
          {t('thankYouMessage')}
        </p>

        {/* Client Summary Card */}
        {client && (
          <Card className="mb-8 text-left animate-fade-in delay-300" data-testid="client-summary-card">
            <CardContent className="p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">Récapitulatif</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Nom</span>
                  <span className="font-medium">{client.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Email</span>
                  <span className="font-medium">{client.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Secteur</span>
                  <span className="font-medium capitalize">{client.branch}</span>
                </div>
                <div className="pt-3 border-t">
                  <span className="text-slate-500 block mb-2">Besoins exprimés</span>
                  <p className="text-slate-700">{client.needs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-2 gap-4 animate-fade-in delay-400">
          <Button
            onClick={handleGenerateQuote}
            disabled={generatingQuote || !client}
            className="bg-primary hover:bg-primary-hover text-white rounded-full py-6 gap-2"
            data-testid="generate-quote-btn"
          >
            {generatingQuote ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <FileText className="w-5 h-5" />
                {t('generateQuote')}
              </>
            )}
          </Button>
          
          <Link to="/appointment">
            <Button variant="outline" className="w-full rounded-full py-6 gap-2 border-2" data-testid="book-appointment-btn">
              <Calendar className="w-5 h-5" />
              {t('bookAppointment')}
            </Button>
          </Link>
        </div>

        {/* Back to Home */}
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline mt-8">
          <ArrowRight className="w-4 h-4 rotate-180" />
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
};
